import { useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  Button,
  Box,
  Chip,
  Menu,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import SearchIcon from '@material-ui/icons/Search';
import UserRoleDetailsSection from '../shared/RoleAndPermissions/UserRoleDetailsSection';
import UserPermissionSection from '../shared/RoleAndPermissions/UserPermissionSection';
import USER_ROLES from 'components/Users/constants';
import { getVisiblePermissionCount } from 'utils/permissionUtils';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
  compactHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
  },
  importButton: {
    textTransform: 'none',
    flexShrink: 0,
  },
  formContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: 'white',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    maxHeight: 'calc(70vh - 200px)',
    minHeight: 300,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: 4,
      '&:hover': {
        background: '#a8a8a8',
      },
    },
  },
  infoBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: 6,
    flex: 1,
    marginRight: theme.spacing(2),
  },
  infoText: {
    fontSize: 14,
    color: '#856404',
    fontWeight: 500,
  },
  selectedAccountsBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#e8f4fd',
    border: '1px solid #b3d9ff',
    borderRadius: 6,
    marginBottom: theme.spacing(2),
  },
  selectedAccountsTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1565c0',
    marginBottom: theme.spacing(1),
  },
  accountsChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  accountChip: {
    backgroundColor: '#1976d2',
    color: 'white',
    fontSize: 12,
    height: 24,
  },
  menuSearch: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    padding: theme.spacing(1),
    borderBottom: '1px solid #e0e0e0',
    marginBottom: theme.spacing(0.5),
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  highlightText: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '1px 2px',
    borderRadius: 2,
    fontWeight: 600,
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    minHeight: 'auto',
  },
  accountInfo: {
    width: '100%',
    overflow: 'hidden',
  },
  accountName: {
    fontWeight: 500,
    fontSize: 14,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  accountDetails: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const Step4AccountSettings = ({
  selectedAccounts = [],
  onSubmit,
  validationErrors = {},
  setValidationErrors,
  isCreateUserFlow = false,
}) => {
  const classes = useStyles();
  const { en: { titles, btn } } = useSelector((state) => state.i18n);
  const { userAccounts } = useSelector((state) => state.userAccountManagement);
  const { isDistributionSponsor } = useSelector((state) => state.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');


  // Check if user has existing accounts to import from
  const hasExistingAccounts = userAccounts && userAccounts.length > 0;

  // Filter accounts based on search query
  const filteredAccounts = userAccounts?.filter(account => {
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase();
    const accountName = (account.accountBusinessName || account.name || '').toLowerCase();
    const accountRole = (account.role || '').toLowerCase();
    return accountName.includes(searchLower) || accountRole.includes(searchLower);
  }) || [];

  // Default values for new accounts
  const getDefaultValues = () => ({
    role: USER_ROLES.ASSISTANT.toLowerCase(),
    providerTitle: '',
    providerNPINumber: '',
    userPermissions: {
      managesUsers: false,
      receiveClaimsEmails: false,
      administerClaims: false,
      administerClaimReports: false,
      receiveSurveysEmails: false,
      imminentRiskEmails: false,
      managesAlert: false,
    }
  });

  const handleImportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleImportClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleImportFromAccount = (account) => {
    // Import settings from selected account
    const importedValues = {
      role: account.role?.toLowerCase() || USER_ROLES.ASSISTANT.toLowerCase(),
      providerTitle: account.providerTitle || '',
      providerNPINumber: account.providerNPINumber || '',
      userPermissions: {
        managesUsers: account.userPermissions?.managesUsers || false,
        receiveClaimsEmails: account.userPermissions?.receiveClaimsEmails || false,
        administerClaims: account.userPermissions?.administerClaims || false,
        administerClaimReports: account.userPermissions?.administerClaimReports || false,
        receiveSurveysEmails: account.userPermissions?.receiveSurveysEmails || false,
        imminentRiskEmails: account.userPermissions?.imminentRiskEmails || false,
        managesAlert: account.userPermissions?.managesAlert || false,
      }
    };

    setFormValues(importedValues);
    handleImportClose();
  };

  // Function to highlight matching text
  const highlightText = (text, query) => {
    if (!query.trim()) {
      return text;
    }

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = regex.test(part);
          return isMatch ? (
            <span key={index} className={classes.highlightText}>
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  };

  const handleFormSubmit = (values) => {
    // Apply settings to all selected accounts
    const accountsWithSettings = selectedAccounts.map(account => ({
      ...account,
      ...values,
      userPermissions: values.userPermissions,
    }));

    // Call parent onSubmit
    if (onSubmit) {
      onSubmit(accountsWithSettings);
    }
  };

  // Auto-update parent when selectedAccounts change with default values
  useEffect(() => {
    if (selectedAccounts.length > 0) {
      const defaultValues = getDefaultValues();
      const accountsWithSettings = selectedAccounts.map(account => ({
        ...account,
        ...defaultValues,
        userPermissions: defaultValues.userPermissions,
      }));

      // Auto-update parent with default settings
      if (onSubmit) {
        onSubmit(accountsWithSettings);
      }
    }
  }, [selectedAccounts]);

  return (
    <div className={classes.container}>
      {/* Selected accounts display */}
      {/*{isDistributionSponsor ? (*/}
      {/*  // For DS accounts, show multiple selected accounts*/}
      {/*  <Box className={classes.selectedAccountsBox}>*/}
      {/*    <Typography className={classes.selectedAccountsTitle}>*/}
      {/*      {titles.settingsWillBeAppliedTo || `Settings will be applied to ${selectedAccounts.length} selected accounts:`}*/}
      {/*    </Typography>*/}
      {/*    <div className={classes.accountsChips}>*/}
      {/*      {selectedAccounts.map((account, index) => (*/}
      {/*        <Chip*/}
      {/*          key={`${account.uuid || account.businessName}-${index}`}*/}
      {/*          label={account.accountBusinessName || account.businessName || account.name}*/}
      {/*          className={classes.accountChip}*/}
      {/*          size="small"*/}
      {/*        />*/}
      {/*      ))}*/}
      {/*    </div>*/}
      {/*  </Box>*/}
      {/*) : (*/}
      {/*  // For non-DS accounts, show current account info more subtly*/}
      {/*  selectedAccounts.length > 0 && (*/}
      {/*    <Box className={classes.selectedAccountsBox}>*/}
      {/*      <Typography className={classes.selectedAccountsTitle}>*/}
      {/*        {titles.configuringUserFor || 'Configuring user for account:'}*/}
      {/*      </Typography>*/}
      {/*      <div className={classes.accountsChips}>*/}
      {/*        <Chip*/}
      {/*          label={selectedAccounts[0]?.accountBusinessName || selectedAccounts[0]?.businessName || selectedAccounts[0]?.name}*/}
      {/*          className={classes.accountChip}*/}
      {/*          size="small"*/}
      {/*        />*/}
      {/*      </div>*/}
      {/*    </Box>*/}
      {/*  )*/}
      {/*)}*/}

      {/* Compact header with info and import button - only show if there's content */}
      {(isDistributionSponsor || (hasExistingAccounts && !isCreateUserFlow)) && (
        <div className={classes.compactHeader}>
          {isDistributionSponsor && (
            <Box className={classes.infoBox}>
              <Typography className={classes.infoText}>
                {titles.accountSettingsInfo || 'Configure role and permissions that will be applied to all selected accounts'}
              </Typography>
            </Box>
          )}

          {hasExistingAccounts && !isCreateUserFlow && (
            <>
              <Button
                variant="outlined"
                color="primary"
                className={classes.importButton}
                startIcon={<ImportExportIcon />}
                onClick={handleImportClick}
              >
                {titles.importFromExisting || 'Import from Existing Account'}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleImportClose}
                PaperProps={{
                  style: {
                    maxHeight: 400,
                    width: 350,
                  },
                }}
              >
                {/* Search field */}
                <div className={classes.menuSearch}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search accounts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {/* Filtered accounts list */}
                {filteredAccounts.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery ? 'No accounts found' : 'No accounts available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  filteredAccounts.map((account) => (
                    <MenuItem
                      key={account.uuid || account.id}
                      onClick={() => handleImportFromAccount(account)}
                      className={classes.menuItem}
                    >
                      <div className={classes.accountInfo}>
                        <Typography className={classes.accountName}>
                          {highlightText(account.accountBusinessName || account.name || '', searchQuery)}
                        </Typography>
                        <Typography className={classes.accountDetails}>
                          {highlightText(account.role || '', searchQuery)} • {getVisiblePermissionCount(account.userPermissions)} permissions
                        </Typography>
                      </div>
                    </MenuItem>
                  ))
                )}
              </Menu>
            </>
          )}
        </div>
      )}

      {/* Form */}
      <div className={classes.formContainer}>
        <Formik
          key={JSON.stringify(formValues)} // Re-initialize when importing
          initialValues={formValues || getDefaultValues()}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {(formik) => {
            // Auto-update parent when form values change
            useEffect(() => {
              if (selectedAccounts.length > 0) {
                const accountsWithSettings = selectedAccounts.map(account => ({
                  ...account,
                  ...formik.values,
                  userPermissions: formik.values.userPermissions,
                }));

                // Auto-update parent
                if (onSubmit) {
                  onSubmit(accountsWithSettings);
                }
              }
            }, [formik.values, selectedAccounts]);

            return (
              <Form id="create-user-account-settings-form">
                {/* User Role Details Section */}
                <UserRoleDetailsSection
                  formik={formik}
                  currentAccount={{}} // Empty object as we're creating new
                  backendErrors={validationErrors}
                />

                <Box my={3} />

                {/* User Permissions Section */}
                <UserPermissionSection
                  values={formik.values}
                  backendErrors={validationErrors}
                  setBackendErrors={setValidationErrors}
                  isOwnerAccDS={false}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default Step4AccountSettings;
